/**
 * Einstellungen dieses Geraets: Darstellung, Ton, Vibration.
 *
 * Sie gehoeren zum Geraet und nicht zum Benutzer: Ein Handy im Einsatz geht
 * durch viele Haende, und ob es piept, entscheidet, wer es gerade haelt. Sie
 * liegen deshalb im `localStorage`, brauchen kein Netz und wirken sofort.
 */

import { useSyncExternalStore } from "react";

export type Darstellung = "automatisch" | "hell" | "dunkel";

export interface Einstellungen {
  darstellung: Darstellung;
  /** Ton nach einem Scan (CLAUDE.md §7: ohne Hinsehen wissen, ob es geklappt hat). */
  ton: boolean;
  vibration: boolean;
  /** Zuletzt gewaehlte Bandbreite des Labelmakers, in mm. */
  band: 6 | 9 | 12 | 18 | 24;
  /**
   * Scannen einzeln oder in eine Sammelliste (ADR 0013 §5, §14). Je Geraet:
   * Wer am Dienstabend Kisten packt, will beim naechsten Oeffnen gleich
   * wieder sammeln.
   */
  scanmodus: "einzel" | "mehrfach";
  /** Name unter dem QR-Code oder daneben. */
  anordnung: "darunter" | "daneben";
  /**
   * Den Stammplatz auf Packstueck- und Geraete-Etiketten drucken, ab 18 mm
   * Band (ADR 0013 §12, ADR 0017). Abwaehlbar: Wer oft umpackt, will kein
   * Etikett, das nach dem naechsten Umpacken nicht mehr stimmt.
   */
  stammplatzDrucken: boolean;
}

const SCHLUESSEL = "thw-einstellungen";

const VORGABE: Einstellungen = {
  darstellung: "automatisch",
  ton: true,
  vibration: true,
  band: 24,
  anordnung: "darunter",
  stammplatzDrucken: true,
  scanmodus: "einzel",
};

let stand: Einstellungen = lesen();
const beobachter = new Set<() => void>();

function lesen(): Einstellungen {
  try {
    const roh = globalThis.localStorage.getItem(SCHLUESSEL);
    if (roh === null) return VORGABE;
    return { ...VORGABE, ...(JSON.parse(roh) as Partial<Einstellungen>) };
  } catch {
    // Privates Fenster oder gesperrter Speicher: dann eben die Vorgabe.
    return VORGABE;
  }
}

export function einstellungen(): Einstellungen {
  return stand;
}

export function einstellungenSetzen(teil: Partial<Einstellungen>): void {
  stand = { ...stand, ...teil };
  try {
    globalThis.localStorage.setItem(SCHLUESSEL, JSON.stringify(stand));
  } catch {
    // Wirkt trotzdem bis zum Neuladen.
  }
  darstellungAnwenden();
  for (const b of beobachter) b();
}

export function useEinstellungen(): Einstellungen {
  return useSyncExternalStore(
    (b) => {
      beobachter.add(b);
      return () => beobachter.delete(b);
    },
    () => stand,
  );
}

const dunkelAmGeraet = globalThis.matchMedia("(prefers-color-scheme: dark)");

/**
 * Setzt die Klasse `dark` am Wurzelelement.
 *
 * Laeuft vor dem ersten Zeichnen (siehe `main.tsx`), damit die Seite nicht
 * erst hell aufblitzt -- nachts im Einsatz blendet das.
 */
export function darstellungAnwenden(): void {
  const dunkel =
    stand.darstellung === "dunkel" ||
    (stand.darstellung === "automatisch" && dunkelAmGeraet.matches);
  globalThis.document.documentElement.classList.toggle("dark", dunkel);
}

// Stellt jemand das Geraet um, waehrend die Anwendung offen ist, zieht
// „automatisch" mit.
dunkelAmGeraet.addEventListener("change", darstellungAnwenden);
